<template>
    <v-footer
        class="order-footer no-transition"
        color="white"
        height="80px"
        fixed
        inset
        app
    >
        <v-bottom-navigation
            :v-model="bottomNav"
            background-color="transparent"
            height="80px"
            grow
        >
            <v-btn
                v-for="option in options.primary"
                :key="option.value"
                class="bottom-nav-item"
                :class="option.classes"
                text
                :value="option.value"
                :disabled="option.disabled"
                @click="selectOption(option.value)"
            >
                <span>{{ option.text }}</span>
                <v-icon>{{ option.icon }}</v-icon>
            </v-btn>
        </v-bottom-navigation>

        <v-bottom-sheet v-model="showBottomSheet">
            <div class="order-options">
                <template v-for="(optionsSection, index) in options.secondary">
                    <v-list
                        v-if="optionsSection.items.length"
                        :key="index"
                    >
                        <v-subheader>{{ optionsSection.title }}</v-subheader>

                        <v-list-item
                            v-for="option in optionsSection.items"
                            :key="option.value"
                            class="bottom-sheet-item"
                            :class="option.classes"
                            :disabled="option.disabled"
                            @click="selectOption(option.value)"
                        >
                            <v-list-item-avatar
                                class="mr-3"
                                :class="{ 'icon-disabled': option.disabled }"
                            >
                                <v-avatar size="32px" tile>
                                    <v-icon :class="option.classes">{{ option.icon }}</v-icon>
                                </v-avatar>
                            </v-list-item-avatar>

                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </template>
            </div>
        </v-bottom-sheet>
    </v-footer>
</template>

<script>
import CashRegister from '@/models/CashRegister';
import EinvoiceService from '@/services/EinvoiceService';
import { mapGetters } from 'vuex';

export default {
    name: 'OrderFooter',

    data: () => ({
        bottomNav: '',
        showBottomSheet: false
    }),

    computed: {
        ...mapGetters(['curOrder', 'unpaidOrders', 'curTable', 'trainingMode', 'splitReceipt', 'integrations']),

        options() {
            const cash = {
                available: true,
                value: 'cash',
                text: this.$t('general.cash'),
                icon: 'fal fa-money-bill',
                disabled: this.paymentTypeDisabled('cash')
            }
            const creditCard = {
                available: true,
                value: 'credit_card',
                text: this.$t('general.credit_card_short'),
                icon: 'fal fa-credit-card',
                disabled: this.paymentTypeDisabled('credit_card')
            }
            const creditCardBusiness = {
                available: locale === 'me',
                value: 'credit_card_business',
                text: this.$t('general.credit_card_business'),
                icon: 'fal fa-credit-card',
                disabled: this.paymentTypeDisabled('credit_card_business'),
                classes: {}
            }
            const invoice = {
                available: true,
                value: 'invoice',
                text: this.$t('orders.payment_types.invoice'),
                icon: 'fal fa-file-alt',
                disabled: this.paymentTypeDisabled('invoice'),
                classes: {}
            }
            const einvoice = {
                available: this.einvoiceOnly,
                value: 'einvoice',
                text: this.$tc('general.einvoice', 1),
                icon: 'fal fa-file-contract',
                disabled: this.paymentDisabled || !this.einvoiceEnabled,
                classes: {}
            }
            const advance = {
                available: !this.trainingMode && !this.einvoiceOnly,
                value: 'advance',
                text: this.$t('general.advance'),
                icon: 'fal fa-calendar-check',
                disabled: this.paymentTypeDisabled('advance'),
                classes: {}
            }
            const otherPayments = {
                available: !this.einvoiceOnly,
                value: 'other_payments',
                text: this.$t('general.other_payments'),
                icon: 'fal fa-money-check',
                disabled: this.paymentDisabled,
                classes: {}
            }
            const proforma = {
                available: true,
                value: 'proforma',
                text: this.$tc('general.proforma', 1),
                icon: 'fal fa-file-invoice',
                disabled: this.orderEmpty || this.unpaidOrders.length > 0,
                classes: {}
            }
            const discount = {
                available: true,
                value: 'discount',
                text: this.$t('general.discount'),
                icon: 'fal fa-percent',
                disabled: !this.allows('allow_discount') || (locale === 'me' && !this.newItemsAdded)
            }
            const client = {
                available: true,
                value: 'client',
                text: this.$tc('general.buyer', 2),
                icon: 'fal fa-user-circle',
                disabled: false
            }
            const note = {
                available: true,
                value: 'note',
                text: this.$t('general.note'),
                icon: 'fal fa-comment-lines',
                disabled: !this.curOrder,
                classes: {}
            }
            const reference = {
                available: locale === 'sr' && this.unpaidOrders.length == 0,
                value: 'reference',
                text: this.$t('general.reference'),
                icon: 'fal fa-link',
                disabled: !this.curOrder,
                classes: {}
            }
            const options = {
                available: true,
                value: 'options',
                text: this.$t('general.options'),
                icon: 'fal fa-ellipsis-h',
                disabled: false
            }
            const cancelOrder = {
                available: true,
                value: 'cancel_order',
                text: this.$t('general.cancel_order'),
                icon: 'fal fa-ban',
                disabled: !this.allows('allow_cancel_order') || this.orderEmpty,
                classes: { 'error--text': this.allows('allow_cancel_order') && !this.orderEmpty }
            }
            const sendToRoom = {
                available: this.integrations.ota_sync,
                value: 'send_to_room',
                text: this.$t('integrations.ota_sync.room_send'),
                icon: 'fal fa-bed',
                disabled: this.paymentDisabled || !this.isOnline,
            }
            const issueOrder = {
                available: true,
                value: 'order',
                text: this.$t('general.issue_order'),
                icon: 'fal fa-check',
                disabled: this.issueDisabled
            }
            const back = {
                available: true,
                value: 'back',
                text: this.$t('general.back'),
                icon: 'fal fa-times',
                disabled: false
            }

            if(this.noncashOnly) {
                if(locale === 'sr') {
                    return {
                        primary: [einvoice, invoice, options, discount, back].filter(o => o.available),

                        secondary: [{
                            title: this.$t('general.other_options'),
                            items: [proforma, client, note, cancelOrder].filter(o => o.available)
                        }]
                    }
                }

                return {
                    primary: [invoice, proforma, options, issueOrder, back].filter(o => o.available),

                    secondary: [{
                        title: this.$t('general.payment'),
                        items: [creditCardBusiness, advance, otherPayments].filter(o => o.available)
                    }, {
                        title: this.$t('general.other_options'),
                        items: [discount, client, note, cancelOrder].filter(o => o.available)
                    }]
                }
            }

            return {
                primary: [cash, creditCard, options, issueOrder, back].filter(o => o.available),
                secondary: [{
                    title: this.$t('general.payment'),
                    items: [invoice, creditCardBusiness, proforma, advance, otherPayments, einvoice].filter(o => o.available)
                }, {
                    title: this.$t('general.other_options'),
                    items: [discount, client, sendToRoom, note, reference, cancelOrder].filter(o => o.available)
                }]
            }
        },

        curCashRegister() {
            return CashRegister.current()
        },

        orderEmpty() {
            return !this.curOrder || !this.curOrder.items || !this.curOrder.items.length;
        },

        paymentDisabled() {
            if(this.splitReceipt.active) return this.orderEmpty;

            if(this.orderEmpty) {
                return this.unpaidOrders.length === 0;
            }

            if(this.unpaidOrders.length > 0) return true;

            const hasItemsWithZeroPrice = this.curOrder.items.some(item => item.getUnitPrice().toUnit() === 0);
            if(hasItemsWithZeroPrice) return true;

            const hasItemsWithNegativeCount = this.curOrder.items.some(item => item.getCount() < 0);
            const allItemsHavNegativeCounts = this.curOrder.items.every(item => item.getCount() < 0);
            if(hasItemsWithNegativeCount && !allItemsHavNegativeCounts) return true;

            if(this.curOrder.refersToAdvance()) return false

            return this.curOrder.getLeftToPay().toUnit() == 0;
        },

        issueDisabled() {
            if(this.orderEmpty) return true;
            if(this.splitReceipt.active) return true;

            const hasItemsWithNegativeCount = this.curOrder.items.some(item => item.getCount() < 0);
            const allItemsHavNegativeCounts = this.curOrder.items.every(item => item.getCount() < 0);
            if(hasItemsWithNegativeCount && !allItemsHavNegativeCounts) return true;

            return !this.newItemsAdded || !this.curTable;
        },

        newItemsAdded() {
            return this.curOrder && this.curOrder.items.length > 0;
        },

        noncashOnly() {
            return !!this.tenant?.venue?.settings?.noncash_only
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        },
    },

    methods: {
        paymentTypeDisabled(type) {
            if(!this.curCashRegister) return true;

            if(this.paymentDisabled) return true;

            if(this.noncashOnly && ['cash', 'credit_card'].includes(type)) return true;

            if(type === 'advance' && (this.curOrder?.refersToAdvance() || this.unpaidOrders.length > 0)) return true;

            if(this.curOrder?.refersToAdvance() && this.curOrder.referredAdvanceOrders()[0].isEinvoice()) return true;

            return !this.curCashRegister.allowsPayment(type);
        },

        selectOption(option) {
            if(['cash', 'credit_card', 'invoice', 'credit_card_business', 'order', 'advance'].includes(option)) {
                this.payAction(option);
            }

            if(option === 'options') this.showBottomSheet = true;
            if(option === 'discount') this.showOrderDiscount();
            if(option === 'client') this.selectClient();
            if(option === 'other_payments') this.showPaymentMethods();
            if(option === 'proforma') this.showInvoiceProformaDialog();
            if(option === 'einvoice') this.showEinvoiceDialog();
            if(option === 'note') this.addNote();
            if(option === 'send_to_room') this.sendToRoom();
            if(option === 'reference') this.showOrderReferenceDialog();
            if(option === 'cancel_order') this.cancelOrder();
            if(option === 'back') this.eventBus.$emit('order-go-back');
        },

        payAction(method) {
            this.showBottomSheet = false;

            if(this.curCashRegister.depositRequired()) {
                this.$router.push('tables').catch(err => {})
                return
            }

            if(['credit_card_business', 'order'].includes(method)) {
                this.eventBus.$emit('order-init-action', { paymentType: method });
            }

            if(method === 'cash') {
                const addToDebtEnabled = locale === 'sr' && !this.curCashRegister.allowsPayment('credit_card');
                if(addToDebtEnabled || this.tenant.venue.settings.cash_change_enabled) {
                    this.eventBus.$emit('order-toggle-dialog', { dialog: 'cash' })
                } else {
                    this.eventBus.$emit('order-init-action', { paymentType: method });
                }
            }

            if(method === 'credit_card') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'creditCard' })
            }

            if(method === 'invoice') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'invoice' })
            }

            if(method === 'advance') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'advance' })
            }
        },

        showInvoiceProformaDialog() {
            if(this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'invoiceProforma' });
        },

        showEinvoiceDialog() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'einvoice' });
        },

        cancelOrder() {
            if (!this.allows('allow_cancel_order')) {
                return;
            }

            this.showBottomSheet = false;
            this.eventBus.$emit('order-cancel-order');
        },

        showOrderDiscount() {
            if (!this.allows('allow_discount')) {
                return;
            }

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'discount' });
        },

        showPaymentMethods() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'paymentMethods' });
        },

        showOrderReferenceDialog() {
            if(this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'orderReference' });
        },

        selectClient() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'selectClient' });
        },

        addNote() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'note' });
        },

        sendToRoom() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'sendToRoom' });
        },
    }
};
</script>

<style lang="scss" scoped>
.order-footer {
    padding: 0px;
    z-index: 4;

    .bottom-nav-item {
        max-width: 100% !important;
        min-width: 60px;

        &:not(.v-btn--active) {
            color: #333333 !important;
        }
    }
}

.order-options {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
    background: white;

    .v-list {
        min-width: 200px;

        .bottom-sheet-item {
            height: 55px;
            padding: 0 0 0 10px;

            .icon-disabled {
                opacity: 0.5;
            }
        }
    }
}
</style>
